// Markdown seguro para o corpo das notificacoes: parser proprio, minimo, que
// produz uma arvore de nos (nunca HTML). O componente SafeMarkdown renderiza
// os nos como elementos React, entao nao existe caminho para injetar HTML.
//
// Suporta: titulos (#, ##, ###), paragrafos, listas (- * 1.), separador (---),
// imagem sozinha na linha ![alt](url), negrito, italico, `codigo` e links.
// Links: so https:// ou caminho interno /app/... ; imagens: so https do
// storage publico do proprio projeto (prefixo injetado). O resto vira texto.

export type InlineNode =
  | { readonly type: "text"; readonly value: string }
  | { readonly type: "strong"; readonly children: readonly InlineNode[] }
  | { readonly type: "em"; readonly children: readonly InlineNode[] }
  | { readonly type: "code"; readonly value: string }
  | { readonly type: "link"; readonly href: string; readonly external: boolean; readonly children: readonly InlineNode[] };

export type BlockNode =
  | { readonly type: "heading"; readonly level: 1 | 2 | 3; readonly children: readonly InlineNode[] }
  | { readonly type: "paragraph"; readonly children: readonly InlineNode[] }
  | { readonly type: "list"; readonly ordered: boolean; readonly items: ReadonlyArray<readonly InlineNode[]> }
  | { readonly type: "image"; readonly src: string; readonly alt: string }
  | { readonly type: "hr" };

export const MAX_MARKDOWN_LENGTH = 20_000;
const INTERNAL_PATH = /^\/app(\/[A-Za-z0-9/_-]*)?$/;
const INLINE_TOKEN = /\*\*([^*\n]+)\*\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)|\*([^*\n]+)\*|_([^_\n]+)_/g;

/** Link permitido: https com host valido ou rota interna do painel. */
export function safeHref(raw: string): { href: string; external: boolean } | null {
  if (INTERNAL_PATH.test(raw)) return { href: raw, external: false };
  if (!raw.startsWith("https://")) return null;
  try {
    // raw ja comeca com https:// e o parser exige host: so falta recusar credenciais.
    const url = new URL(raw);
    return !url.username && !url.password ? { href: url.href, external: true } : null;
  } catch {
    return null;
  }
}

/** Imagem permitida: https e dentro do prefixo do storage do projeto. */
export function safeImageSrc(raw: string, allowedPrefix: string | null): string | null {
  if (!allowedPrefix || !allowedPrefix.startsWith("https://") || !raw.startsWith(allowedPrefix)) return null;
  const rest = raw.slice(allowedPrefix.length);
  // Sem voltar diretorio nem trocar de host via caracteres especiais.
  return rest !== "" && !/(\.\.|\\|[\s"'<>])/.test(rest) ? raw : null;
}

function inlineToken(match: RegExpExecArray, depth: number): InlineNode {
  const [, strong, code, linkText, linkHref, em1, em2] = match;
  if (strong !== undefined) return { type: "strong", children: parseInline(strong, depth + 1) };
  if (code !== undefined) return { type: "code", value: code };
  if (linkText !== undefined && linkHref !== undefined) {
    const safe = safeHref(linkHref);
    const children = parseInline(linkText, depth + 1, false);
    return safe ? { type: "link", href: safe.href, external: safe.external, children } : { type: "text", value: linkText };
  }
  return { type: "em", children: parseInline(em1 ?? em2 ?? "", depth + 1) };
}

/** Parser de linha. depth limita aninhamento; allowLinks evita link dentro de link. */
export function parseInline(text: string, depth = 0, allowLinks = true): InlineNode[] {
  if (depth > 3) return text ? [{ type: "text", value: text }] : [];
  const nodes: InlineNode[] = [];
  const pattern = new RegExp(INLINE_TOKEN.source, "g");
  let last = 0;
  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    const isLink = match[3] !== undefined;
    if (isLink && !allowLinks) continue;
    if (match.index > last) nodes.push({ type: "text", value: text.slice(last, match.index) });
    nodes.push(inlineToken(match, depth));
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push({ type: "text", value: text.slice(last) });
  return nodes;
}

const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^\s*[-*]\s+(.+)$/;
const ORDERED = /^\s*\d{1,3}[.)]\s+(.+)$/;
const IMAGE = /^!\[([^\]\n]*)\]\(([^)\s]+)\)$/;

function listItem(line: string): { ordered: boolean; text: string } | null {
  const bullet = BULLET.exec(line);
  if (bullet?.[1]) return { ordered: false, text: bullet[1] };
  const ordered = ORDERED.exec(line);
  return ordered?.[1] ? { ordered: true, text: ordered[1] } : null;
}

function singleLineBlock(line: string, imagePrefix: string | null): BlockNode | null {
  if (/^-{3,}$/.test(line)) return { type: "hr" };
  const heading = HEADING.exec(line);
  if (heading?.[1] && heading[2]) {
    return { type: "heading", level: heading[1].length as 1 | 2 | 3, children: parseInline(heading[2]) };
  }
  const image = IMAGE.exec(line);
  if (image?.[2] !== undefined) {
    const src = safeImageSrc(image[2], imagePrefix);
    return src ? { type: "image", src, alt: image[1] ?? "" } : null;
  }
  return null;
}

/** Converte o markdown em blocos. Nunca lanca; entrada longa e truncada. */
export function parseMarkdown(source: string, imagePrefix: string | null): BlockNode[] {
  const lines = source.slice(0, MAX_MARKDOWN_LENGTH).replace(/\r\n?/g, "\n").split("\n");
  const blocks: BlockNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: InlineNode[][] } | null = null;

  const flush = (): void => {
    if (paragraph.length > 0) blocks.push({ type: "paragraph", children: parseInline(paragraph.join(" ")) });
    if (list) blocks.push({ type: "list", ordered: list.ordered, items: list.items });
    paragraph = [];
    list = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const item = listItem(rawLine);
    if (item) {
      if (paragraph.length > 0 || (list && list.ordered !== item.ordered)) flush();
      list = list ?? { ordered: item.ordered, items: [] };
      list.items.push(parseInline(item.text));
      continue;
    }
    const block = line === "" ? null : singleLineBlock(line, imagePrefix);
    if (line === "" || block || IMAGE.test(line)) {
      flush();
      if (block) blocks.push(block);
      continue;
    }
    if (list) flush();
    paragraph.push(line);
  }
  flush();
  return blocks;
}

/** Prefixo publico do storage do projeto (ex.: https://x.supabase.co/storage/v1/object/public/). */
export function storagePublicPrefix(supabaseUrl: string | undefined): string | null {
  if (!supabaseUrl) return null;
  try {
    const url = new URL(supabaseUrl);
    return url.protocol === "https:" ? `${url.origin}/storage/v1/object/public/` : null;
  } catch {
    return null;
  }
}
